import { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";

import type { SubNavTab } from "@/workloads/scope";

interface SubNavProps {
  tabs: SubNavTab[];
  active: string;
  onChange: (id: string) => void;
  /** Optional badge counts per tab (e.g. number of pools). */
  badges?: Record<string, number | undefined>;
  title?: string;
  subtitle?: string;
  /**
   * If provided, the title becomes click-to-edit. Pass a setter that
   * persists the new label (e.g. `setServiceLabel(svc.id, next)`); empty
   * strings should clear the override and revert to the slug.
   */
  onRenameTitle?: (next: string) => void;
  /** Hover hint shown alongside the rename pencil (e.g. immutable slug). */
  renameHint?: string;
}

export function SubNav({
  tabs,
  active,
  onChange,
  badges,
  title,
  subtitle,
  onRenameTitle,
  renameHint,
}: SubNavProps) {
  return (
    <nav className="subnav" aria-label="section">
      {(title || subtitle) && (
        <div className="subnav__header">
          {subtitle && (
            <div className="subnav__eyebrow" title={subtitle}>
              {subtitle}
            </div>
          )}
          {title &&
            (onRenameTitle ? (
              <EditableTitle
                value={title}
                hint={renameHint}
                onSave={onRenameTitle}
              />
            ) : (
              <div className="subnav__title">{title}</div>
            ))}
        </div>
      )}
      <ul className="subnav__list">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === active;
          const badge = badges?.[t.id];
          return (
            <li key={t.id}>
              <button
                type="button"
                className={`subnav__item${isActive ? " active" : ""}`}
                onClick={() => onChange(t.id)}
              >
                <Icon aria-hidden="true" />
                <span className="subnav__label">{t.label}</span>
                {badge !== undefined && badge > 0 && (
                  <span className="subnav__badge">{badge}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

interface EditableTitleProps {
  value: string;
  hint?: string;
  onSave: (next: string) => void;
}

/**
 * The renameable title that sits at the top of the SubNav. Click the
 * title (or the pencil) to enter edit mode; Enter / blur saves, Esc
 * cancels. When the upstream label changes (e.g. via another tab) we
 * resync — but only while not in edit mode so we don't clobber the
 * operator's typing.
 */
function EditableTitle({ value, hint, onSave }: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="subnav__title-input"
        autoFocus
        value={draft}
        aria-label="rename"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        onBlur={commit}
        onFocus={(e) => e.currentTarget.select()}
      />
    );
  }

  return (
    <button
      type="button"
      className="subnav__title subnav__title--editable"
      onClick={() => setEditing(true)}
      title={hint ? `Click to rename — ${hint}` : "Click to rename"}
    >
      <span className="subnav__title-text">{value}</span>
      <Pencil className="subnav__title-edit" size={11} aria-hidden="true" />
    </button>
  );
}
